import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAudioLevelDetectionProps {
  mediaStream: MediaStream | null;
  isRecording: boolean;
  onNoAudioDetected: () => void;
  silenceThreshold?: number;
  silenceDetectionDelay?: number;
}

interface AudioLevelData {
  currentLevel: number;
  isAudioDetected: boolean;
}

export function useAudioLevelDetection({
  mediaStream,
  isRecording,
  onNoAudioDetected,
  silenceThreshold = 0.01,
  silenceDetectionDelay = 5000,
}: UseAudioLevelDetectionProps): AudioLevelData {
  const [currentLevel, setCurrentLevel] = useState<number>(0);
  const [isAudioDetected, setIsAudioDetected] = useState<boolean>(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastAudioDetectedRef = useRef<number>(Date.now());

  const startSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }

    silenceTimerRef.current = setTimeout(() => {
      const timeSinceLastAudio = Date.now() - lastAudioDetectedRef.current;
      if (timeSinceLastAudio >= silenceDetectionDelay && isRecording) {
        onNoAudioDetected();
      }
    }, silenceDetectionDelay);
  }, [silenceDetectionDelay, onNoAudioDetected, isRecording]);

  const analyzeAudioLevel = useCallback(() => {
    if (!analyserRef.current || !isRecording) {
      return;
    }

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    const sum = dataArray.reduce((acc, value) => acc + value, 0);
    const average = sum / dataArray.length;
    const normalizedLevel = average / 255;

    setCurrentLevel(normalizedLevel);

    const hasAudio = normalizedLevel > silenceThreshold;
    setIsAudioDetected(hasAudio);

    if (hasAudio) {
      lastAudioDetectedRef.current = Date.now();
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    } else if (!silenceTimerRef.current && isRecording) {
      startSilenceTimer();
    }

    animationFrameRef.current = requestAnimationFrame(analyzeAudioLevel);
  }, [isRecording, silenceThreshold, startSilenceTimer]);

  useEffect(() => {
    if (!mediaStream || !isRecording) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      return;
    }

    try {
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const source =
        audioContextRef.current.createMediaStreamSource(mediaStream);

      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;

      source.connect(analyserRef.current);

      lastAudioDetectedRef.current = Date.now();
      startSilenceTimer();
      analyzeAudioLevel();
    } catch (error) {
      console.error('Error setting up audio analysis:', error);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== 'closed'
      ) {
        audioContextRef.current.close();
      }
    };
  }, [mediaStream, isRecording, analyzeAudioLevel, startSilenceTimer]);

  useEffect(() => {
    return () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    currentLevel,
    isAudioDetected,
  };
}
